<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import {
  getAccountDomain,
  removeAccountDomain,
  updateAccountDomain,
} from 'dashboard/api/accountDomain';

const route = useRoute();
const { t } = useI18n();
const accountId = computed(() => Number(route.params.accountId));
const loading = ref(true);
const saving = ref(false);
const hostname = ref('');
const state = ref(null);

const statusLabel = computed(() => {
  const statusKeys = {
    pending_dns: 'PENDING_DNS',
    provisioning_tls: 'PROVISIONING_TLS',
    active: 'ACTIVE',
    failed: 'FAILED',
    removing: 'REMOVING',
  };
  const key = statusKeys[state.value?.domain?.status] || 'NOT_STARTED';
  return t(`ACCOUNT_DOMAIN.STATUS.${key}`);
});

const load = async () => {
  loading.value = true;
  try {
    const { data } = await getAccountDomain(accountId.value);
    state.value = data;
    hostname.value = data.domain?.hostname || '';
  } catch {
    useAlert(t('ACCOUNT_DOMAIN.ALERTS.LOAD_FAILED'));
  } finally {
    loading.value = false;
  }
};

const remove = async () => {
  saving.value = true;
  try {
    const { data } = await removeAccountDomain(accountId.value);
    state.value = { ...state.value, ...data, domain: data.domain };
    useAlert(t('ACCOUNT_DOMAIN.ALERTS.REMOVE_STARTED'));
  } catch {
    useAlert(t('ACCOUNT_DOMAIN.ALERTS.REMOVE_FAILED'));
  } finally {
    saving.value = false;
  }
};

const save = async () => {
  saving.value = true;
  try {
    const { data } = await updateAccountDomain(accountId.value, hostname.value.trim());
    state.value = { ...state.value, ...data, domain: data.domain };
    useAlert(t('ACCOUNT_DOMAIN.ALERTS.SAVED'));
  } catch (error) {
    const code = error.response?.data?.error;
    useAlert(
      code === 'custom_domain_unavailable'
        ? t('ACCOUNT_DOMAIN.ALERTS.UNAVAILABLE')
        : t('ACCOUNT_DOMAIN.ALERTS.SAVE_FAILED')
    );
  } finally {
    saving.value = false;
  }
};

const copyTarget = async () => {
  await navigator.clipboard.writeText(state.value?.cname_target || 'domains.urtaco.com');
  useAlert(t('ACCOUNT_DOMAIN.ALERTS.COPIED'));
};

onMounted(load);
</script>

<template>
  <SettingsLayout
    :is-loading="loading"
    :loading-message="t('ACCOUNT_DOMAIN.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="t('ACCOUNT_DOMAIN.TITLE')"
        :description="t('ACCOUNT_DOMAIN.DESCRIPTION')"
      />
    </template>

    <div class="mx-auto flex w-full max-w-3xl flex-col gap-6 px-6 py-5 text-start">
      <ol class="flex flex-col divide-y divide-n-weak rounded-lg border border-n-weak bg-n-solid-2">
        <li class="flex gap-4 p-5">
          <span class="flex size-8 shrink-0 items-center justify-center rounded-md bg-n-alpha-2 font-medium">1</span>
          <div class="flex-1">
            <h3 class="font-medium text-n-slate-12">
              {{ t('ACCOUNT_DOMAIN.STEPS.HOSTNAME_TITLE') }}
            </h3>
            <p class="mt-1 text-sm text-n-slate-11">
              {{ t('ACCOUNT_DOMAIN.STEPS.HOSTNAME_DESCRIPTION') }}
            </p>
            <input v-model="hostname" dir="ltr" class="mt-3 h-10 w-full rounded-lg border border-n-strong px-3 text-left" placeholder="support.store.com" />
          </div>
        </li>
        <li class="flex gap-4 p-5">
          <span class="flex size-8 shrink-0 items-center justify-center rounded-md bg-n-alpha-2 font-medium">2</span>
          <div class="flex-1">
            <h3 class="font-medium text-n-slate-12">
              {{ t('ACCOUNT_DOMAIN.STEPS.CNAME_TITLE') }}
            </h3>
            <p class="mt-1 text-sm text-n-slate-11">
              {{ t('ACCOUNT_DOMAIN.STEPS.CNAME_DESCRIPTION') }}
            </p>
            <div class="mt-3 flex items-center gap-2" dir="ltr">
              <code class="min-w-0 flex-1 rounded-md bg-n-alpha-2 px-3 py-2 text-sm">{{ state?.cname_target || 'domains.urtaco.com' }}</code>
              <Button
                icon="i-lucide-copy"
                variant="outline"
                size="sm"
                :label="t('ACCOUNT_DOMAIN.ACTIONS.COPY')"
                @click="copyTarget"
              />
            </div>
          </div>
        </li>
        <li class="flex gap-4 p-5">
          <span class="flex size-8 shrink-0 items-center justify-center rounded-md bg-n-alpha-2 font-medium">3</span>
          <div class="flex-1">
            <h3 class="font-medium text-n-slate-12">
              {{ t('ACCOUNT_DOMAIN.STEPS.VERIFY_TITLE') }}
            </h3>
            <p class="mt-1 text-sm text-n-slate-11">
              {{ t('ACCOUNT_DOMAIN.STEPS.VERIFY_DESCRIPTION') }}
            </p>
            <p class="mt-3 font-medium text-n-slate-12">{{ statusLabel }}</p>
          </div>
        </li>
      </ol>
      <div class="flex justify-end gap-3">
        <Button
          v-if="state?.domain"
          variant="ghost"
          color="ruby"
          :label="t('ACCOUNT_DOMAIN.ACTIONS.REMOVE')"
          icon="i-lucide-trash-2"
          @click="remove"
        />
        <Button
          variant="outline"
          :label="t('ACCOUNT_DOMAIN.ACTIONS.REFRESH')"
          icon="i-lucide-refresh-cw"
          @click="load"
        />
        <Button
          :is-loading="saving"
          :label="t('ACCOUNT_DOMAIN.ACTIONS.SAVE')"
          icon="i-lucide-check"
          @click="save"
        />
      </div>
    </div>
  </SettingsLayout>
</template>
